import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, downloadCsv } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, ArrowUpDown, ArrowUp, ArrowDown, Download } from "lucide-react";

const CATS = ["Active & Outdoors", "Food & Drink", "Games & Competition", "Arts & Entertainment", "Experiences", "Wellness & Mindfulness", "Nightlife & Social"];
const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const EMPTY = { name: "", category: CATS[0], street: "", city: "Tampa", state: "FL", zip_code: "", website: "", phone: "", reservation_url: "", notes: "", price_level: "$$", active: true, lat: null, lon: null };

export default function Venues() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [pq, setPq] = useState("");
    const [results, setResults] = useState([]);
    const { data } = useQuery({ queryKey: ["admin-venues"], queryFn: async () => (await api.get("/admin/venues")).data });
    const [vq, setVq] = useState("");
    const [sort, setSort] = useState({ key: "name", dir: "asc" });
    const toggleSort = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));
    const SortHead = ({ k, children }) => (
        <TableHead><button type="button" data-testid={`venue-sort-${k}`} onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 font-600 hover:text-zinc-900">{children}{sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}</button></TableHead>
    );
    const rows = useMemo(() => {
        let arr = [...(data || [])];
        if (vq) { const s = vq.toLowerCase(); arr = arr.filter((v) => [v.name, v.category, v.city, v.state, v.zip_code].some((x) => (x || "").toLowerCase().includes(s))); }
        const val = (v) => {
            if (sort.key === "active") return v.active ? "active" : "inactive";
            if (sort.key === "outing_count") return v.outing_count || 0;
            if (sort.key === "last_used" || sort.key === "created_at") return v[sort.key] || "";
            return (v[sort.key] || "").toString().toLowerCase();
        };
        arr.sort((a, b) => { const av = val(a), bv = val(b); if (av < bv) return sort.dir === "asc" ? -1 : 1; if (av > bv) return sort.dir === "asc" ? 1 : -1; return 0; });
        return arr;
    }, [data, vq, sort]);

    const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
    const searchPlaces = async () => {
        if (!pq.trim()) return;
        try {
            const { data } = await api.get("/admin/venues/places-search", { params: { q: pq, lat: 27.95, lon: -82.45 } });
            setResults(data);
            if (!data.length) toast.info("No Google Places results.");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const importPlace = (r) => {
        setEdit(null);
        setForm({ ...EMPTY, name: r.name, street: r.address || "", city: form.city, website: r.website || "",
                  phone: r.phone || "", price_level: r.price_level || "$$", lat: r.lat, lon: r.lon });
        setOpen(true);
    };
    const openEdit = (v) => { setEdit(v); setForm({ ...EMPTY, ...v }); setOpen(true); };
    const save = async () => {
        try {
            if (edit) await api.put(`/admin/venues/${edit.id}`, form); else await api.post("/admin/venues", form);
            toast.success("Saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["admin-venues"] });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <AdminHeader title="Venues" subtitle="Approved venues the platform can recommend for outings." />
                <div className="flex shrink-0 gap-2">
                    <Button variant="outline" onClick={() => downloadCsv("/admin/export/venues.csv", "ssc-venues.csv")} data-testid="export-venues-csv"><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
                    <Button onClick={openNew} data-testid="venue-new"><Plus className="mr-1 h-4 w-4" /> New venue</Button>
                </div>
            </div>
            <div className="mb-4 rounded-lg border border-zinc-200 bg-white p-3">
                <p className="mb-2 text-xs font-600 text-zinc-500">Search Google Places to import a real venue</p>
                <div className="flex gap-2">
                    <Input data-testid="places-query" value={pq} onChange={(e) => setPq(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchPlaces()} placeholder="e.g. axe throwing in Tampa" />
                    <Button onClick={searchPlaces} data-testid="places-search-btn">Search</Button>
                </div>
                {results.length > 0 && (
                    <div className="mt-2 max-h-56 space-y-1 overflow-auto">
                        {results.map((r, i) => (
                            <button key={i} data-testid={`place-result-${i}`} onClick={() => importPlace(r)}
                                className="flex w-full items-center justify-between rounded border border-zinc-200 px-3 py-2 text-left text-sm hover:bg-zinc-50">
                                <span><span className="font-600">{r.name}</span> <span className="text-zinc-500">— {r.address}</span></span>
                                <span className="text-xs text-zinc-500">{r.rating ? `★ ${r.rating}` : ""} {r.price_level}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="mb-3 flex max-w-sm items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
                <Search className="h-4 w-4 text-zinc-400" />
                <input data-testid="venue-search" value={vq} onChange={(e) => setVq(e.target.value)} placeholder="Find by name, city, or state" className="w-full bg-transparent text-sm outline-none" />
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
                <Table>
                    <TableHeader><TableRow><SortHead k="name">Name</SortHead><SortHead k="category">Category</SortHead><SortHead k="city">City</SortHead><SortHead k="created_at">Added</SortHead><SortHead k="outing_count">Outings</SortHead><SortHead k="last_used">Last used</SortHead><SortHead k="active">Status</SortHead></TableRow></TableHeader>
                    <TableBody>
                        {rows.map((v) => (
                            <TableRow key={v.id} data-testid={`venue-row-${v.id}`} className="cursor-pointer" onClick={() => openEdit(v)}>
                                <TableCell className="font-500">{v.name}</TableCell>
                                <TableCell className="text-zinc-500">{v.category}</TableCell>
                                <TableCell>{v.city}</TableCell>
                                <TableCell className="whitespace-nowrap text-zinc-500" data-testid={`venue-added-${v.id}`}>{fmtDate(v.created_at)}</TableCell>
                                <TableCell data-testid={`venue-outings-${v.id}`}>{v.outing_count || 0}</TableCell>
                                <TableCell className="whitespace-nowrap text-zinc-500" data-testid={`venue-lastused-${v.id}`}>{fmtDate(v.last_used)}</TableCell>
                                <TableCell><Badge tone={v.active ? "green" : "gray"}>{v.active ? "active" : "inactive"}</Badge></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
                    <DialogHeader><DialogTitle>{edit ? "Edit venue" : "New venue"}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><label className="text-sm font-600">Name</label><Input data-testid="venue-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
                        <div>
                            <label className="text-sm font-600">Category</label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="venue-category" className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><label className="text-sm font-600">Price level</label><Input value={form.price_level} onChange={(e) => setForm({ ...form, price_level: e.target.value })} className="mt-1" /></div>
                        <div className="col-span-2"><label className="text-sm font-600">Street</label><Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} className="mt-1" /></div>
                        <div><label className="text-sm font-600">City</label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><label className="text-sm font-600">State</label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1" /></div>
                            <div><label className="text-sm font-600">ZIP</label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="mt-1" /></div>
                        </div>
                        <div><label className="text-sm font-600">Website</label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1" /></div>
                        <div><label className="text-sm font-600">Phone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                    </div>
                    <DialogFooter><Button onClick={save} data-testid="venue-save">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
